<script setup lang="ts">
import { SancareOcticon, SancareSidePanel } from '@sancare/typescript-commons'
import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import { HealthDataDocument } from '@sancare/typescript-commons/src/types/health-data'
import _ from 'lodash'
import numeral from 'numeral'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { createFalseJustification, deleteFalseJustification, fetchFalseJustifications } from '@/misc/stayFalseJustifications'
import { flattenSuggestionJustifications, prefetchMcoStayJustifications } from '@/misc/stayJustifications'
import { prefetchMcoStaySuggestions } from '@/misc/staySuggestions'
import type { FalseJustificationItem, FlagJustificationPayload, McoStaySuggestionItem, SuggestionJustification } from '@/misc/types'
import router from '@/router'
import ApiJustificationSummary from '@/stay-displayer/diagnosis-lists/ApiJustificationSummary.vue'
import GroupingBadge from '@/stay-displayer/grouping/common/GroupingBadge.vue'
import {
  formatDate,
  formatGroupingCost,
  formatPatientName,
  formatWeekNumber
} from '@/stay-list/stay-list-table/helpers/line/lineContentFormatters'
import { redirectToStayDisplayer } from '@/stay-list/stay-list-table/helpers/line/lineHelper'
import { ListMcoStay } from '@/stay-list/stay-list-table/mco/types'
import { useWildcardFolding } from '@/stay-list/stay-quick-view/composables/useWildcardFolding'
import { buildAggregatedDasSuggestions } from '@/stay-list/stay-quick-view/dasSuggestionAggregator'
import {
  buildAdditionalCodingsPayload,
  buildGroupageSimulationRequest,
  buildPerSuggestionGroupageSimulationRequest,
  GroupageSimulation,
  toGroupageSimulation
} from '@/stay-list/stay-quick-view/store/dto'
import { simulateGroupage } from '@/stay-list/stay-quick-view/store/groupageSimulation'
import { updateMcoStayStatus } from '@/stay-list/stay-quick-view/store/updateStayStatus'
import { DEFAULTS } from '@/stay-list/storeDefaultState'
import { useStore } from '@/store'
import { ModeEnum } from '@/store/modules/settings/types'
import { StayStatusEnum } from '@/store/modules/stay/types'

const props = defineProps<{
  stay: ListMcoStay | null
  mode: ModeEnum
}>()

const open = defineModel<boolean>('open', { default: false })

const store = useStore()
const route = useRoute()
const { t } = useI18n()
const appSettings = computed(() => store.getters.flatAppSettings)
const settingsUrlQuery = computed(() => store.getters.settingsUrlQuery)

const patientName = computed(() => (props.stay ? formatPatientName(props.stay.patient) : ''))
const stayStart = computed(() => (props.stay ? formatDate(props.stay.stayStart) : ''))
const stayEnd = computed(() => (props.stay ? formatDate(props.stay.stayEnd) : ''))
const weekNumber = computed(() => (props.stay ? formatWeekNumber(props.stay.stayStart) : ''))
const ghm = computed(() => props.stay?.standardModeGhm ?? null)
const ghs = computed(() => props.stay?.standardModeGhs ?? null)
// Quick mode (and so this aside) is only ever available in France (see quickMode.ts), so unlike
// McoStayListLine.vue's currentValue there is no CH/DRG branch to consider here.
const value = computed(() => (props.stay ? formatGroupingCost(ghm.value !== null, props.stay.standardModeValue) : ''))
const simulatedValue = computed(() => (groupageSimulation.value
  ? formatGroupingCost(groupageSimulation.value.ghm !== null, groupageSimulation.value.value)
  : ''))
const moneyGap = computed(() => (groupageSimulation.value && props.stay
  ? groupageSimulation.value.value - props.stay.standardModeValue
  : 0))
function formatMoneyGap(gap: number): string {
  return `${numeral(gap).format('+ 0,0').replace(/,/g, ' ')} €`
}
const formattedMoneyGap = computed(() => formatMoneyGap(moneyGap.value))
const isValidated = computed(() => props.stay?.status === StayStatusEnum.VALIDATED)
const validatedOn = computed(() => {
  if (!props.stay?.validationDate) {
    return ''
  }
  const d = dayjs(props.stay.validationDate)
  return t('stayList.stayQuickView.validatedOn', { date: d.format('DD/MM/YYYY'), time: d.format('HH:mm') })
})

// Mirrors useStayRedirection's viewStay, called directly against redirectToStayDisplayer instead
// of going through that composable: the aside's target stay changes over its lifetime, whereas
// useStayRedirection binds a single fixed path at setup time (one instance per staylist row).
function viewStay(event: MouseEvent, forceNewWindow: boolean | null = null) {
  if (!props.stay) {
    return
  }
  redirectToStayDisplayer(event, forceNewWindow, router, {
    routeFullPath: route.fullPath,
    newRoute: {
      path: `/mco/id/${props.stay.id}`,
      query: {
        ...route.query,
        ...settingsUrlQuery.value,
      },
    },
  })
}

const rawSuggestions = ref<McoStaySuggestionItem[]>([])
const suggestionJustifications = ref<Record<string, SuggestionJustification>>({})
const falseJustifications = ref<FalseJustificationItem[]>([])
const isLoadingSuggestions = ref(false)
// Which suggestion's justification popin (if any) is currently open — at most one at a time,
// mirrors SuggestedDiagnosis.vue's per-card showJustificationSummary but shared across the list
// since every popin here comes from the same click target (the suggestion's paper icon).
const openJustificationReference = ref<string | null>(null)
// Mirrors DiagnosisListStandardMode.vue's isRevaluating (parseInt(ghm.level), NaN-guarded) — used by
// dasSuggestionAggregator.ts to gate over-threshold DAS visibility in revalo mode.
const ghmLevel = computed(() => {
  const level = ghm.value ? parseInt(ghm.value.level, 10) : NaN
  return Number.isNaN(level) ? null : level
})
const dasSuggestions = computed(() => (props.stay
  ? buildAggregatedDasSuggestions(rawSuggestions.value, appSettings.value, props.stay.hasInitialCoding, props.mode, ghmLevel.value)
  : []))
const healthDataForJustifications = computed(() => ({ falseJustifications: falseJustifications.value }))

// BUILD-809: montant simulé par card, indépendant de la sélection multiple (groupageSimulation
// plus bas) — un scénario par suggestion affichée, y compris les enfants de wildcard repliés
// (otherWildcardChildren) pour que leur montant soit déjà prêt si l'utilisateur déplie le groupe.
const suggestionSimulations = ref<Map<string, GroupageSimulation>>(new Map())
let suggestionSimulationRequestId = 0

async function loadSuggestionSimulations() {
  const requestId = ++suggestionSimulationRequestId
  const stay = props.stay
  const dasReferences = dasSuggestions.value.flatMap((suggestion) => [
    suggestion.reference,
    ...suggestion.otherWildcardChildren.map((child) => child.reference),
  ])

  if (!stay || dasReferences.length === 0) {
    if (requestId === suggestionSimulationRequestId) {
      suggestionSimulations.value = new Map()
    }
    return
  }

  try {
    const request = buildPerSuggestionGroupageSimulationRequest(props.mode, stay.rums, dasReferences)
    const response = await simulateGroupage(stay.id, request, store.commit)
    if (requestId === suggestionSimulationRequestId) {
      suggestionSimulations.value = new Map(dasReferences.map((reference, index) => [reference, toGroupageSimulation(response[index])]))
    }
  } catch (err) {
    if (requestId === suggestionSimulationRequestId) {
      // eslint-disable-next-line no-console
      console.error(err)
      suggestionSimulations.value = new Map()
    }
  }
}

const selectedDasReferences = ref<Set<string>>(new Set())
const hasSelection = computed(() => selectedDasReferences.value.size > 0)

// Pre-formats each card's amount once per relevant change (suggestionSimulations/selection),
// instead of recomputing the gap/format/highlight on every access in the template.
type SuggestionAmountDisplay = {
  text: string
  positive: boolean
  negative: boolean
}
const suggestionAmountByReference = computed<Map<string, SuggestionAmountDisplay>>(() => {
  const stay = props.stay
  if (!stay) {
    return new Map()
  }

  return new Map([...suggestionSimulations.value].map(([reference, simulation]) => {
    const gap = simulation.value - stay.standardModeValue
    const isSelected = selectedDasReferences.value.has(reference)
    return [reference, { text: formatMoneyGap(gap), positive: !isSelected && gap > 0, negative: !isSelected && gap < 0 }]
  }))
})

const {
  expandedWildcards,
  toggleWildcardExpansion,
  resetWildcardExpansion,
  setWildcardDefault,
  visibleWildcardMembers,
  foldedWildcardStackBars,
} = useWildcardFolding(selectedDasReferences)

// When deselecting the last selected member of a wildcard group, that member becomes the group's
// new default diagnostic — shown in its place once folded back with nothing selected (UX request:
// don't revert to the original representative).
function toggleDasSelection(reference: string) {
  const wasSelected = selectedDasReferences.value.has(reference)
  const next = new Set(selectedDasReferences.value)
  if (wasSelected) {
    next.delete(reference)
  } else {
    next.add(reference)
  }
  selectedDasReferences.value = next

  if (!wasSelected) {
    return
  }

  const group = dasSuggestions.value.find((suggestion) => (
    suggestion.otherWildcardChildren.length > 0
    && (suggestion.reference === reference || suggestion.otherWildcardChildren.some((child) => child.reference === reference))
  ))
  if (!group) {
    return
  }

  const groupReferences = [group.reference, ...group.otherWildcardChildren.map((child) => child.reference)]
  const stillHasSelection = groupReferences.some((groupReference) => next.has(groupReference))
  if (!stillHasSelection) {
    setWildcardDefault(group.reference, reference)
  }
}

// Fetches suggestions, justifications and false justifications each time a different stay opens in
// the aside — unlike the full stay page (misc/stayDocumentsMerge.ts), the staylist has no
// previously-displayed-stay cache, and the aside never loads the full stay these prefetch alongside.
// Also called after a successful status update (BUILD-807) to drop the just-applied suggestions
// from the list, at no extra cost since it's a normal re-fetch.
async function loadSuggestions() {
  if (!props.stay) {
    rawSuggestions.value = []
    suggestionJustifications.value = {}
    falseJustifications.value = []
    openJustificationReference.value = null
    return
  }

  isLoadingSuggestions.value = true
  try {
    // onlyNonCodedDas=true: unlike the page séjour, this quick view only ever shows non-coded DAS
    // suggestions — an intentional scope boundary, see
    // wiki/pages/fonctionnalites-codage/tableau-de-bord-listes-travail.md, "Mode rapide".
    // includeBelowThreshold=false (unlike the page séjour): the quick view shows the backend's
    // standard-mode-threshold suggestions/justifications only, no below-threshold predictions.
    const [suggestionsResponse, justificationsResponse, falseJustificationsResponse] = await Promise.all([
      prefetchMcoStaySuggestions('rssid', props.stay.rssId, store.commit, true, false),
      prefetchMcoStayJustifications('id', String(props.stay.id), store.commit, false),
      fetchFalseJustifications(`/api/stay/${props.stay.id}/false-justifications?idType=id`, store.commit),
    ])
    rawSuggestions.value = suggestionsResponse.sancareSuggestions
    suggestionJustifications.value = flattenSuggestionJustifications(justificationsResponse)
    falseJustifications.value = falseJustificationsResponse.falseJustifications
    // Not awaited: has its own loading/error handling and shouldn't delay the suggestions list itself.
    loadSuggestionSimulations()
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)
  } finally {
    isLoadingSuggestions.value = false
  }
}

watch(() => props.stay?.id, (stayId) => {
  rawSuggestions.value = []
  selectedDasReferences.value = new Set()
  suggestionJustifications.value = {}
  falseJustifications.value = []
  openJustificationReference.value = null
  suggestionSimulationRequestId += 1
  suggestionSimulations.value = new Map()
  resetWildcardExpansion()
  if (stayId === undefined) {
    return
  }
  loadSuggestions()
}, { immediate: true })

// Read-only preview (BUILD-805): no persistence, just a call to the BUILD-804 simulation route
// re-run on every selection change. The per-RUM payload construction (dialyse = all RUMs of the
// stay, revalo = only the major/groupant RUM) is resolved front-side, per the ticket's decision.
const groupageSimulation = ref<GroupageSimulation | null>(null)
const isSimulatingGroupage = ref(false)
let simulationRequestId = 0

// Debounced so rapidly toggling several suggestions doesn't fire one simulation call per click —
// but only the actual fetch is debounced: isSimulatingGroupage is flipped synchronously by the
// watcher below so the loading feedback isn't delayed by the debounce.
const debouncedSimulateGroupage = _.debounce(async (selection: Set<string>) => {
  const requestId = ++simulationRequestId
  const stay = props.stay

  try {
    const request = stay ? buildGroupageSimulationRequest(props.mode, stay.rums, [...selection]) : { scenarios: [] }
    if (!stay || request.scenarios.length === 0 || request.scenarios[0].rums.length === 0) {
      // eslint-disable-next-line no-console
      console.warn('[StayQuickView] No RUM to target for the groupage simulation', { mode: props.mode, rums: JSON.stringify(stay?.rums) })
      if (requestId === simulationRequestId) {
        groupageSimulation.value = null
      }
      return
    }

    const response = await simulateGroupage(stay.id, request, store.commit)
    if (requestId === simulationRequestId) {
      groupageSimulation.value = toGroupageSimulation(response[0])
    }
  } catch (err) {
    if (requestId === simulationRequestId) {
      // eslint-disable-next-line no-console
      console.error(err)
      groupageSimulation.value = null
    }
  } finally {
    if (requestId === simulationRequestId) {
      isSimulatingGroupage.value = false
    }
  }
}, 300)

watch(selectedDasReferences, (selection) => {
  // Back to the initial state: nothing to compute, so the previous simulation disappears right
  // away, with no loading state / header opacity (those are only for an actual recalculation).
  if (selection.size === 0) {
    debouncedSimulateGroupage.cancel()
    simulationRequestId += 1
    groupageSimulation.value = null
    isSimulatingGroupage.value = false
    return
  }

  isSimulatingGroupage.value = true
  debouncedSimulateGroupage(selection)
})

function toggleJustification(reference: string) {
  openJustificationReference.value = openJustificationReference.value === reference ? null : reference
}

// Only clears if `reference` is still the open one: switching directly from one suggestion's popin
// to another sets openJustificationReference to the new reference, then closes the old dropdown —
// whose own @hide must not clobber the new selection.
function handleDropdownHide(reference: string) {
  if (openJustificationReference.value === reference) {
    openJustificationReference.value = null
  }
}

async function refreshFalseJustifications() {
  if (!props.stay) {
    return
  }
  try {
    const response = await fetchFalseJustifications(`/api/stay/${props.stay.id}/false-justifications?idType=id`, store.commit)
    falseJustifications.value = response.falseJustifications
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)
  }
}

async function flagJustification(payload: FlagJustificationPayload) {
  if (!props.stay) {
    return
  }
  try {
    await createFalseJustification(props.stay.id, payload, store.commit)
    await refreshFalseJustifications()
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)
  }
}

async function unflagJustification({ justificationId }: { justificationId: number, docType: string, docId: number }) {
  if (!props.stay) {
    return
  }
  try {
    await deleteFalseJustification(justificationId, store.commit)
    await refreshFalseJustifications()
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)
  }
}

// Mirrors viewStay: no document viewer here, so the target is shown by navigating to the stay page
// instead of updating local state — query params picked up by StayDisplayerPage.vue's
// preselectJustificationTarget(). Also mirrors its ctrl/middle-click handling via redirectToStayDisplayer.
function viewJustificationTarget(mainContent: HealthDataDocument, scrollTarget?: { docId: number, offset: number | null } | null, event?: MouseEvent) {
  if (!props.stay) {
    return
  }
  const newRoute = {
    path: `/mco/id/${props.stay.id}`,
    query: {
      ...route.query,
      ...settingsUrlQuery.value,
      docType: mainContent.type,
      ...(mainContent.targetId !== null ? { docId: String(mainContent.targetId) } : {}),
      ...(mainContent.drugEventPrestationType ? { drugEventPrestationType: mainContent.drugEventPrestationType } : {}),
      ...(scrollTarget?.offset !== null && scrollTarget?.offset !== undefined ? { wordOffset: String(scrollTarget.offset) } : {}),
    },
  }

  if (event) {
    redirectToStayDisplayer(event, event.button === 1, router, { routeFullPath: route.fullPath, newRoute })
    return
  }
  window.sessionStorage.setItem('stayListRoute', route.fullPath)
  router.push(newRoute)
}

// Applies the selected DAS suggestions and validates the stay in one transactional backend call.
// The stay-list item is patched in place (aside + row share the same reactive object) instead of
// a full refetch. No "review" button here: dropped from scope pending a separate UX ticket.
const isUpdatingStatus = ref(false)
const isValidateDisabled = computed(() => (
  !props.stay || props.stay.status === StayStatusEnum.VALIDATED || isUpdatingStatus.value
))

async function validateStay() {
  const stay = props.stay
  if (!stay || isValidateDisabled.value) {
    return
  }

  isUpdatingStatus.value = true
  try {
    const additionalCodings = buildAdditionalCodingsPayload(props.mode, stay.rums, [...selectedDasReferences.value])
    const updatedStay = await updateMcoStayStatus(stay.id, StayStatusEnum.VALIDATED, additionalCodings, store.commit)
    store.commit('patchStayListItem', {
      stayId: stay.id,
      patch: {
        status: updatedStay.status,
        standardModeGhm: updatedStay.standardModeGhm,
        standardModeGhs: updatedStay.standardModeGhs,
        standardModeValue: updatedStay.standardModeValue,
        standardModeGroupingError: updatedStay.standardModeGroupingError,
        moneyGap: updatedStay.standardModeValue - updatedStay.firstStandardModeValue,
        validationDate: updatedStay.validationDate,
      },
    })
    // The row no longer matches the current search once "validated" is excluded from it — drop it
    // from the list and keep stayList_count consistent with what a refetch would show. The aside
    // itself is unaffected (still the same stay object) and keeps showing the validation info.
    const showStatus = store.state.stayList.stayListParams.showStatus
    const effectiveShowStatus = Array.isArray(showStatus) && showStatus.length ? showStatus : DEFAULTS().STAYLIST_STATUS
    if (!effectiveShowStatus.includes(StayStatusEnum.VALIDATED)) {
      store.commit('removeStayListItem', stay.id)
    }
    selectedDasReferences.value = new Set()
    await loadSuggestions()
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)
  } finally {
    isUpdatingStatus.value = false
  }
}
</script>

<template>
  <sancare-side-panel
    v-model:open="open"
    width="400px"
  >
    <div
      v-if="stay"
      class="stay-quick-view"
      :class="{ 'stay-quick-view--updating': isUpdatingStatus }"
    >
      <div
        class="stay-quick-view__header pt-5 px-3"
        :class="{ 'stay-quick-view__header--computing': isSimulatingGroupage }"
      >
        <div class="stay-quick-view__id-row">
          <a
            class="d-flex align-items-center fw-bold administrative-number"
            @click.prevent="viewStay($event)"
            @click.middle="viewStay($event, true)"
          >
            <span>#</span>
            <span>{{ stay.administrativeLocalStayId }}</span>
          </a>
          <span class="stay-quick-view__week">{{ weekNumber }}</span>
        </div>
        <div class="stay-quick-view__patient">
          {{ patientName }}
        </div>
        <div class="stay-quick-view__dates mb-2">
          {{ stayStart }} — {{ stayEnd }}
        </div>
        <div
          class="stay-quick-view__badges gap-2"
          :class="{ 'stay-quick-view__badges--muted': hasSelection }"
        >
          <grouping-badge
            v-if="ghm"
            :value="ghm.reference"
            variant="primary"
            size="sm"
            :title="ghm.description"
          />
          <grouping-badge
            v-if="ghs"
            :value="ghs"
            variant="secondary"
            size="sm"
          />
          <span class="stay-quick-view__price ms-auto">{{ value }} €</span>
        </div>
        <!--
            Always rendered — even with nothing selected — at a fixed height (visibility, not v-if,
            toggles its content) so the content below never shifts and risks a misclick.
          -->
        <div class="stay-quick-view__simulation mb-3">
          <div
            class="stay-quick-view__delta"
            :class="[
              {
                'grouping-positive-revaluation': moneyGap > 0,
                'grouping-negative-revaluation': moneyGap < 0,
                'stay-quick-view__simulation-content--hidden': !groupageSimulation || isSimulatingGroupage
              }
            ]"
          >
            {{ groupageSimulation ? formattedMoneyGap : '\u00A0' }}
          </div>
          <div
            class="stay-quick-view__badges gap-2"
            :class="{ 'stay-quick-view__simulation-content--hidden': !groupageSimulation || isSimulatingGroupage }"
          >
            <grouping-badge
              :value="groupageSimulation?.ghm?.reference ?? null"
              variant="primary"
              size="sm"
              :title="groupageSimulation?.ghm?.description"
            />
            <grouping-badge
              :value="groupageSimulation?.ghs ?? null"
              variant="secondary"
              size="sm"
            />
            <span class="stay-quick-view__price ms-auto">{{ groupageSimulation ? simulatedValue : ' ' }} €</span>
          </div>
          <div
            v-if="isSimulatingGroupage"
            name="stayQuickView_groupageSimulationLoader"
            class="stay-quick-view__simulation-loader loader loader-sm"
          />
        </div>
      </div>

      <div class="stay-quick-view__scroll-area px-3 pb-3">
        <div
          v-if="isValidated"
          class="stay-quick-view__validation-info mt-3"
        >
          <div class="grouping-card__validation-info">
            <sancare-octicon
              name="check"
              :width="16"
              :height="16"
            />
            <span>{{ validatedOn }}</span>
          </div>
          <a
            class="stay-quick-view__view-stay-link mt-2"
            @click.prevent="viewStay($event)"
            @click.middle="viewStay($event, true)"
          >
            <span>{{ t('stayList.stayQuickView.viewStay') }}</span>
            <sancare-octicon
              name="arrow-right"
              :width="14"
              :height="14"
            />
          </a>
        </div>

        <div
          v-else
          class="stay-quick-view__suggestions-section mt-3"
        >
          <div
            v-if="isLoadingSuggestions"
            name="stayQuickView_suggestionsLoader"
            class="loader loader-sm"
          />
          <ul
            v-else-if="dasSuggestions.length"
            class="stay-quick-view__suggestions list-unstyled"
          >
            <li
              v-for="suggestion in dasSuggestions"
              :key="suggestion.reference"
              class="stay-quick-view__suggestion-group"
            >
              <!-- SancareSidePanel teleports to <body> and closes on v-on-click-outside checking
                   el.contains(event.target). The popper below keeps floating-vue's default teleport
                   too (also to <body>, needed so it can render outside/left of the panel instead of
                   being clipped by its overflow:hidden) — which makes it a DOM sibling of the panel,
                   not a descendant, so every click inside it would otherwise read as "outside" and
                   close the whole panel. @click.stop on the popin itself stops that click from ever
                   reaching the window-level click-outside listener, without affecting its own
                   internal handlers (they run during the same bubble, before this stop applies). -->
              <!--
                BUILD-830: a wildcard (e.g. I48*) shows only its default diagnostic (its first
                valid child, unless overridden — see useWildcardFolding) while folded with nothing
                selected, or the selected member(s) once one is selected. Its other valid children
                stay folded away until unfolded.
              -->
              <div
                v-for="member in visibleWildcardMembers(suggestion)"
                :key="member.reference"
                class="stay-quick-view__suggestion-row gap-2"
              >
                <v-dropdown
                  :shown="openJustificationReference === member.reference"
                  :triggers="[]"
                  placement="left"
                  class="popover-container"
                  popover-class="justification-popover"
                  @hide="handleDropdownHide(member.reference)"
                >
                  <sancare-octicon
                    name="file"
                    :width="16"
                    class="stay-quick-view__suggestion-icon"
                    :class="{ 'stay-quick-view__suggestion-icon--active': openJustificationReference === member.reference }"
                    @click="toggleJustification(member.reference)"
                  />
                  <template #popper>
                    <api-justification-summary
                      :is-loading="isLoadingSuggestions"
                      :justification="suggestionJustifications[`DAS:${member.reference}`] ?? null"
                      :health-data="healthDataForJustifications"
                      label-type="DAS"
                      :label-reference="member.reference"
                      @click.stop
                      @select-health-data="viewJustificationTarget"
                      @flag-justification="flagJustification"
                      @unflag-justification="unflagJustification"
                    />
                  </template>
                </v-dropdown>
                <div
                  class="stay-quick-view__suggestion-card p-2"
                  :class="{ 'stay-quick-view__suggestion-card--selected': selectedDasReferences.has(member.reference) }"
                  @click.stop="toggleDasSelection(member.reference)"
                >
                  <div class="stay-quick-view__suggestion-card-header">
                    <strong>{{ member.reference }}</strong>
                    <span
                      v-if="suggestionAmountByReference.has(member.reference)"
                      class="stay-quick-view__suggestion-amount"
                      :class="{
                        'grouping-positive-revaluation': suggestionAmountByReference.get(member.reference)?.positive,
                        'grouping-negative-revaluation': suggestionAmountByReference.get(member.reference)?.negative
                      }"
                    >
                      {{ suggestionAmountByReference.get(member.reference)?.text }}
                    </span>
                  </div>
                  <p class="stay-quick-view__suggestion-description mb-0">
                    {{ member.description }}
                  </p>
                </div>
              </div>

              <template v-if="suggestion.otherWildcardChildren.length">
                <div
                  v-if="!expandedWildcards.has(suggestion.reference) && foldedWildcardStackBars(suggestion) > 0"
                  class="stay-quick-view__wildcard-fold"
                  @click.stop="toggleWildcardExpansion(suggestion.reference)"
                >
                  <div
                    v-for="bar in foldedWildcardStackBars(suggestion)"
                    :key="bar"
                    class="stay-quick-view__wildcard-stack-bar"
                  />
                  <button
                    type="button"
                    class="stay-quick-view__wildcard-toggle"
                  >
                    <sancare-octicon
                      name="unfold"
                      :width="14"
                      :height="14"
                    />
                  </button>
                </div>
                <button
                  v-else-if="expandedWildcards.has(suggestion.reference)"
                  type="button"
                  class="stay-quick-view__wildcard-toggle stay-quick-view__wildcard-toggle--last"
                  @click.stop="toggleWildcardExpansion(suggestion.reference)"
                >
                  <sancare-octicon
                    name="fold-up"
                    :width="14"
                    :height="14"
                  />
                </button>
              </template>
            </li>
          </ul>
          <p
            v-else
            class="text-muted"
          >
            {{ t('stayList.stayQuickView.noDasSuggestions') }}
          </p>
        </div>
      </div>

      <div
        v-if="!isValidated"
        class="stay-quick-view__footer d-flex justify-content-center align-items-center gap-2 p-3 border-top"
      >
        <div
          class="btn btn-grouping-validation btn-grouping-validation__unvalidated"
          :class="{ 'stay-quick-view__action--disabled': isValidateDisabled }"
          @click="validateStay()"
        >
          <div class="btn__label">
            {{ t('stayList.stayQuickView.validateCoding') }}
          </div>
          <div class="btn__icon">
            <div
              v-if="isUpdatingStatus"
              class="loader loader-xs loader-purple"
            />
            <sancare-octicon
              v-else
              name="arrow-right"
            />
          </div>
        </div>
      </div>
    </div>
  </sancare-side-panel>
</template>

<style scoped lang="scss">
@use 'sass:color';
@import '@sancare/typescript-commons/src/assets/styles/variables';
@import '@/design-system/tokens/colors';

// Unselected suggestion card background — also used for the folded wildcard stack bars, which
// must read as the same shade of blue (UX feedback).
$suggestion-card-bg: color.adjust($sancare-azure-blue, $lightness: 35%);

.stay-quick-view {
  display: flex;
  flex-direction: column;
  height: 100%;

  // Covers the whole aside (header, suggestions/validation info, footer) while validateStay()'s
  // request is in flight, so the button's own loader isn't the only feedback (UX request, BUILD-807).

  &--updating {
    opacity: .5;
  }

  &__scroll-area {
    flex: 1 1 auto;
    overflow-y: auto;
  }

  &__header {
    &--computing {
      opacity: .5;
    }
  }

  &__id-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
  }

  &__week {
    font-weight: 600;
    color: #6c757d;
  }

  &__dates {
    color: #6c757d;
  }

  &__badges {
    display: flex;
    align-items: center;

    // The real/current grouping is greyed out once a simulation (from the selected DAS
    // suggestions) is displayed below it, which then takes over the normal (colored) look.

    &--muted {
      color: #6c757d;

      :deep(.grouping-badge__value) {
        background-color: $grouping-badge-secondary-bg;
      }
    }
  }

  &__price {
    font-weight: 700;
    font-size: 1rem;
  }

  &__delta {
    display: flex;
    justify-content: flex-end;
    font-weight: 700;
  }

  &__simulation {
    position: relative;
  }

  &__simulation-content--hidden {
    visibility: hidden;
  }

  &__simulation-loader {
    position: absolute;
    inset: 0;
    margin: auto;
  }

  &__suggestions {
    display: flex;
    flex-direction: column;
    // Space between distinct diagnostics (each its own suggestion-group below) — smaller, 2px,
    // between members of a same wildcard, see &__suggestion-group.
    gap: 5px;
  }

  &__suggestion-row {
    display: flex;
    align-items: flex-start;
  }

  // Same color as .justification-flag-icon (ApiJustificationSummary.vue).

  &__suggestion-icon {
    flex: 0 0 auto;
    margin-top: .6rem;
    fill: $icon-subtle-purple;
    cursor: pointer;

    &--active {
      fill: $icon-active-blue;
    }
  }

  &__suggestion-card {
    flex: 1 1 auto;
    background-color: $suggestion-card-bg;
    border-radius: .5rem;
    cursor: pointer;

    &--selected {
      background-color: $sancare-azure-blue;
      color: white;
    }
  }

  &__suggestion-card-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: .5rem;
  }

  &__suggestion-amount {
    flex: 0 0 auto;
    font-size: .75rem;
    font-weight: 600;
  }

  &__suggestion-group {
    display: flex;
    flex-direction: column;
    // Space between the rows of a same wildcard (its visible members, and the stacklist below
    // them) — 2px, smaller than the 5px between distinct diagnostics (&__suggestions' gap).
    gap: 2px;
  }

  // BUILD-830: fold/unfold control for a wildcard's other valid children (octicon fold/fold-up —
  // chosen over fold/unfold, too easily confused with one another).

  // One bar per folded child (see foldedWildcardStackBars) — the toggle button is centered on
  // the whole stack, however tall it is, rather than at a fixed offset.

  &__wildcard-fold {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 2px;
    // Left-aligned with the suggestion cards above (16px icon + .5rem gap-2), full width otherwise.
    // No top margin: the 2px gap to the default diagnostic above comes from &__suggestion-group.
    margin: 0 0 0 24px;
    cursor: pointer;
  }

  &__wildcard-stack-bar {
    height: 5px;
    border-radius: 2px;
    background-color: $suggestion-card-bg;
  }

  &__wildcard-toggle {
    position: absolute;
    top: 50%;
    left: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 20px;
    padding: 0;
    background-color: white;
    border: none;
    border-radius: 999px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .2);
    fill: #6c757d;
    cursor: pointer;
    transform: translate(-50%, -50%);

    // The octicon svg is inline by default, which leaves it sitting on its text baseline (a bit of
    // descender space below) instead of truly centered in the flex box.

    :deep(svg) {
      display: block;
    }

    // The fold (collapse) button sits on its own, after the last unfolded child, not over a stack.

    &--last {
      position: static;
      margin: 0 auto;
      transform: none;
    }
  }

  &__view-stay-link {
    display: flex;
    align-items: center;
    gap: 4px;
    width: fit-content;
    margin-left: auto;
    color: $sancare-azure-blue;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }

    svg {
      fill: currentcolor;
    }
  }

  &__footer {
    flex: 0 0 auto;
  }

  &__action--disabled {
    opacity: .5;
    pointer-events: none;
  }
}

// .administrative-number's base look (weight) is global (assets/styles/general-stay-data.scss),
// shared with AdminStayHeader.vue on the page séjour. Here it's rendered as an anchor rather than
// a plain div, so this overrides the anchor defaults (link blue, underline) plus a smaller
// font-size specific to the aside (UX request: 1.1rem instead of the page séjour's 20px).

.administrative-number {
  color: inherit;
  font-size: 1.1rem;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: inherit;
    text-decoration: none;
  }
}
</style>
